import type { ReactNode } from "react";

import { Breadcrumb, type BreadcrumbItem } from "@/components/breadcrumb";

interface PageHeaderProps {
  /**
   * Breadcrumb trail for nested pages — pass two or more crumbs
   * (`Settings › Flows`). Omit on top-level pages (Dashboard, Clients,
   * Settings, …): a lone crumb repeating the title reads as a second header.
   */
  breadcrumbs?: BreadcrumbItem[];
  /** Page title — always rendered as the uniform `text-3xl font-bold` h1. */
  title: ReactNode;
  /** A control before the title on the same line, outside the h1 (e.g. a back arrow). */
  leading?: ReactNode;
  /** Status labels after the title on the same line, outside the h1 (e.g. badges). */
  titleAddon?: ReactNode;
  description?: ReactNode;
  /** Right-aligned header actions (doc button, primary CTA, …). */
  actions?: ReactNode;
}

// The canonical page header (DESIGN.md › Layout): breadcrumb, a uniform 30px
// `h1`, muted description, and right-aligned actions. Pages compose it inside
// their `space-y-6` wrapper instead of hand-rolling the h1 block.
export function PageHeader({
  breadcrumbs,
  title,
  leading,
  titleAddon,
  description,
  actions,
}: PageHeaderProps) {
  const heading = <h1 className="text-3xl font-bold">{title}</h1>;
  return (
    <div className="space-y-6">
      {breadcrumbs && <Breadcrumb items={breadcrumbs} />}
      <div className="flex items-start justify-between gap-4">
        <div>
          {leading || titleAddon ? (
            // Kept out of the h1 so the heading's accessible name stays the title alone.
            <div className="flex items-center gap-3">
              {leading}
              {heading}
              {titleAddon}
            </div>
          ) : (
            heading
          )}
          {description && <p className="text-muted-foreground mt-2">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
